import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, Download, Search, ExternalLink } from "lucide-react";
import PageShell from "../components/PageShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// sessionStorage instead of localStorage: token is cleared when the tab closes,
// which keeps the XSS exfil window short. The admin token is also single-purpose
// (read-only data export) and never grants write access.
const TOKEN_KEY = "gl_admin_token";

const BAND_STYLES = {
  green: "bg-[#4ADE80]/10 text-[#4ADE80] border-[#4ADE80]/30",
  yellow: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  red: "bg-red-500/10 text-red-400 border-red-500/30",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ band: "", status: "", q: "" });
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const fetchAll = useCallback(
    async (tok) => {
      setLoading(true);
      try {
        const params = {
          token: tok,
          limit: pageSize,
          skip: page * pageSize,
        };
        if (filter.band) params.band = filter.band;
        if (filter.status) params.status = filter.status;
        if (filter.q) params.q = filter.q;
        const [s, r] = await Promise.all([
          axios.get(`${API}/admin/stats`, { params: { token: tok } }),
          axios.get(`${API}/admin/runs`, { params }),
        ]);
        setStats(s.data);
        setRows(r.data.rows || []);
        setTotal(r.data.total || 0);
        setAuthed(true);
        setAuthError("");
        sessionStorage.setItem(TOKEN_KEY, tok);
      } catch (err) {
        if (err?.response?.status === 401) {
          setAuthError("Invalid admin token.");
          setAuthed(false);
        } else {
          setAuthError(err?.message || "Failed to load admin data.");
        }
      } finally {
        setLoading(false);
      }
    },
    [filter.band, filter.status, filter.q, page],
  );

  // Auto-login if a token is already in sessionStorage on mount.
  useEffect(() => {
    if (token && !authed) {
      fetchAll(token);
    }
  }, [token, authed, fetchAll]);

  // Re-fetch when filters or page change while authed.
  useEffect(() => {
    if (authed) fetchAll(token);
  }, [filter.band, filter.status, page, authed, fetchAll, token]);

  const onSubmitToken = (e) => {
    e.preventDefault();
    if (token.trim()) fetchAll(token.trim());
  };

  const onSignOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setRows([]);
    setStats(null);
  };

  const csvUrl = useMemo(
    () => `${API}/admin/emails.csv?token=${encodeURIComponent(token)}`,
    [token],
  );

  if (!authed) {
    return (
      <PageShell>
        <section className="max-w-md mx-auto px-4 sm:px-6 pt-16 pb-16">
          <form
            onSubmit={onSubmitToken}
            className="gl-card gl-card-active p-6 sm:p-8 text-center"
            data-testid="admin-login-form"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#4ADE80]/10 border border-[#4ADE80]/30 mb-4">
              <Lock size={18} className="text-[#4ADE80]" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Admin Sign-In
            </h1>
            <p className="text-sm text-[#b6c1b8] mt-2">
              Enter the admin token to view captured emails and funnel stats.
            </p>
            <div className="mt-5 text-left space-y-2">
              <label className="gl-label" htmlFor="admin-token">
                Admin Token
              </label>
              <input
                id="admin-token"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste your ADMIN_TOKEN"
                className="gl-input"
                data-testid="admin-token-input"
                autoFocus
              />
              {authError ? (
                <div className="text-xs text-red-400" data-testid="admin-error">
                  {authError}
                </div>
              ) : null}
            </div>
            <button
              type="submit"
              className="gl-cta gl-cta-block mt-5"
              data-testid="admin-signin-button"
            >
              Sign in
            </button>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6c7770] mt-4 font-semibold">
              Token lives in /app/backend/.env (ADMIN_TOKEN)
            </p>
          </form>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="gl-label mb-1">Greenlight Score Admin</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Funnel + Captured Emails
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchAll(token)}
              className="gl-ghost inline-flex items-center gap-2"
              data-testid="admin-refresh-button"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <a
              href={csvUrl}
              className="gl-ghost inline-flex items-center gap-2"
              data-testid="admin-csv-download"
            >
              <Download size={14} />
              Emails CSV
            </a>
            <button onClick={onSignOut} className="gl-ghost" data-testid="admin-signout">
              Sign out
            </button>
          </div>
        </div>

        {/* Stats cards */}
        {stats ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
            <Stat label="Total runs" value={stats.total_runs} />
            <Stat label="Emails captured" value={stats.emails_captured} accent />
            <Stat label="Capture rate" value={`${stats.email_capture_rate}%`} />
            <Stat label="Avg score" value={stats.avg_final_score ?? "n/a"} />
            <Stat label="Green" value={stats.band_counts.green} tone="#4ADE80" />
            <Stat label="Yellow" value={stats.band_counts.yellow} tone="#EAB308" />
            <Stat label="Red" value={stats.band_counts.red} tone="#EF4444" />
            <Stat label="Completed" value={stats.completed_runs} />
          </div>
        ) : null}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
          <div className="flex-1">
            <label className="gl-label block mb-2">Search idea or email</label>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPage(0);
                fetchAll(token);
              }}
              className="relative"
            >
              <input
                value={filter.q}
                onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                placeholder="window washing, jane@gmail.com..."
                className="gl-input pl-11"
                data-testid="admin-search"
              />
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6c7770]"
              />
            </form>
          </div>
          <div>
            <label className="gl-label block mb-2">Band</label>
            <select
              value={filter.band}
              onChange={(e) => {
                setPage(0);
                setFilter({ ...filter, band: e.target.value });
              }}
              className="gl-input"
              data-testid="admin-band-filter"
              style={{ paddingTop: "0.7rem", paddingBottom: "0.7rem" }}
            >
              <option value="">All bands</option>
              <option value="green">Green</option>
              <option value="yellow">Yellow</option>
              <option value="red">Red</option>
            </select>
          </div>
          <div>
            <label className="gl-label block mb-2">Status</label>
            <select
              value={filter.status}
              onChange={(e) => {
                setPage(0);
                setFilter({ ...filter, status: e.target.value });
              }}
              className="gl-input"
              data-testid="admin-status-filter"
              style={{ paddingTop: "0.7rem", paddingBottom: "0.7rem" }}
            >
              <option value="">All</option>
              <option value="complete">Completed</option>
              <option value="provisional">Provisional only</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div
          className="gl-card overflow-hidden p-0"
          data-testid="admin-runs-table"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#6c7770]">
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">When</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Email</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Idea</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Score</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Band</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Proof</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Report</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-10 text-center text-[#6c7770]"
                    >
                      {loading ? "Loading..." : "No runs match these filters yet."}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-[#1a221c] hover:bg-[#0d130e]"
                    >
                      <td className="px-4 py-3 text-[#b6c1b8] whitespace-nowrap">
                        {formatDate(r.created_at)}
                      </td>
                      <td className="px-4 py-3 text-white">
                        {r.email || (
                          <span className="text-[#6c7770]">no email</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white max-w-[320px]">
                        <div className="truncate" title={r.idea_text}>
                          {r.idea_text}
                        </div>
                        {r.category ? (
                          <div className="text-[10px] text-[#4ADE80] uppercase tracking-wider mt-0.5">
                            {r.category}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 font-bold text-white">
                        {r.final_score ?? (
                          <span className="text-[#6c7770]">
                            {r.provisional_score ?? "..."}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.band ? (
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${BAND_STYLES[r.band] || ""}`}
                          >
                            {r.band}
                          </span>
                        ) : (
                          <span className="text-[#6c7770] text-xs">pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#b6c1b8]">
                        {r.earnings_verified_count || 0} verified
                      </td>
                      <td className="px-4 py-3">
                        {r.share_id ? (
                          <a
                            href={`/r/${r.share_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#4ADE80] hover:underline"
                          >
                            view <ExternalLink size={11} />
                          </a>
                        ) : (
                          <span className="text-[#6c7770] text-xs">none</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {total > pageSize ? (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#1a221c] text-xs text-[#b6c1b8]">
              <div>
                Showing {page * pageSize + 1}-
                {Math.min((page + 1) * pageSize, total)} of {total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="gl-ghost px-3 py-1"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={(page + 1) * pageSize >= total}
                  className="gl-ghost px-3 py-1"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </PageShell>
  );
}

function Stat({ label, value, accent = false, tone = null }) {
  return (
    <div
      className="gl-card-lift p-4"
      style={tone ? { borderColor: `${tone}55` } : undefined}
    >
      <div className="gl-label-muted mb-1">{label}</div>
      <div
        className="text-2xl sm:text-3xl font-bold leading-none"
        style={{ color: tone || (accent ? "#4ADE80" : "#fff") }}
      >
        {value}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
